// Updated File: src/pages/admin/AdminDelivery.jsx
// Updates:
// - Added lucide-react icons for better visual hierarchy (Truck, Coins, ShoppingBag, Check, AlertCircle, Loader2, ArrowLeft)
// - Improved UI/UX: Card-based layout, responsive form, subtle animations, better spacing, visual feedback for checkboxes
// - Unique class names prefixed with 'ad-' to avoid conflicts
// - Enhanced loading state with spinner and skeleton
// - Styled error message with icon
// - Form validation feedback (basic)
// - Kept all functionality intact

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { deliveryAPI } from '../../services/api';
import { Truck, Coins, ShoppingBag, Check, AlertCircle, Loader2, ArrowLeft, Settings } from 'lucide-react';
import './css/AdminDelivery.css';

const AdminDelivery = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    delivery_fee: 0,
    min_free_delivery: 0,
    is_free_for_all: false,
    is_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await deliveryAPI.getSettings();
      setSettings({
        delivery_fee: response.data.delivery_fee || 0,
        min_free_delivery: response.data.min_free_delivery || 0,
        is_free_for_all: response.data.is_free_for_all || false,
        is_enabled: response.data.is_enabled || true
      });
    } catch (err) {
      setError('Impossible de charger les parametres de livraison');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (settings.delivery_fee < 0 || settings.min_free_delivery < 0) {
      setError('Les valeurs ne peuvent pas etre negatives');
      return;
    }
    setUpdating(true);
    setError('');
    try {
      await deliveryAPI.updateSettings(settings);
      alert('Parametres de livraison mis a jour !');
    } catch (err) {
      setError('Echec de mise a jour des parametres de livraison');
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : parseFloat(value) || 0
    }));
  };

  if (loading) {
    return (
      <div className="ad-container">
        <div className="ad-loading-container">
          <Loader2 className="ad-loading-spinner" />
          <p className="ad-loading-text">Chargement des parametres de livraison...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ad-container">
      <div className="ad-header">
        <button onClick={() => navigate('/admin')} className="ad-back-btn" aria-label="Retour au tableau de bord">
          <ArrowLeft size={20} />
          Retour au tableau de bord
        </button>
        <div className="ad-header-title">
          <Truck size={28} />
          <h1>Gestion de la livraison</h1>
        </div>
      </div>
      <div className="ad-content">
        {error && (
          <div className="ad-error-alert">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}
        <div className="ad-card">
          <div className="ad-card-header">
            <Settings size={20} />
            <h2>Parametres de livraison</h2>
          </div>
          <form onSubmit={handleSubmit} className="ad-form">
            <div className="ad-form-group">
              <label htmlFor="delivery_fee" className="ad-label">
                <Coins size={16} />
                Frais de livraison fixes (DT)
              </label>
              <input
                type="number"
                id="delivery_fee"
                name="delivery_fee"
                value={settings.delivery_fee}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="ad-input"
                required
              />
              <small className="ad-helper-text">Frais standard de livraison</small>
            </div>
            <div className="ad-form-group">
              <label htmlFor="min_free_delivery" className="ad-label">
                <ShoppingBag size={16} />
                Valeur minimale du panier pour livraison gratuite (DT)
              </label>
              <input
                type="number"
                id="min_free_delivery"
                name="min_free_delivery"
                value={settings.min_free_delivery}
                onChange={handleChange}
                step="0.01"
                min="0"
                className="ad-input"
                required
              />
              <small className="ad-helper-text">Les commandes au-dessus de ce montant ont la livraison gratuite</small>
            </div>
            <div className="ad-form-group ad-checkbox-group">
              <label className="ad-checkbox-label">
                <input
                  type="checkbox"
                  name="is_free_for_all"
                  checked={settings.is_free_for_all}
                  onChange={handleChange}
                />
                <div className="ad-checkbox-custom">
                  {settings.is_free_for_all && <Check size={16} />}
                </div>
                <span>Livraison gratuite pour toutes les commandes</span>
              </label>
              <small className="ad-helper-text">Ignorer le minimum panier pour tous les clients</small>
            </div>
            <div className="ad-form-group ad-checkbox-group">
              <label className="ad-checkbox-label">
                <input
                  type="checkbox"
                  name="is_enabled"
                  checked={settings.is_enabled}
                  onChange={handleChange}
                />
                <div className="ad-checkbox-custom">
                  {settings.is_enabled && <Check size={16} />}
                </div>
                <span>Activer le systeme de livraison</span>
              </label>
              <small className="ad-helper-text">Desactiver pour couper les options de livraison sur tout le site</small>
            </div>
            <button type="submit" className="ad-submit-btn" disabled={updating}>
              {updating ? (
                <>
                  <Loader2 className="ad-loading-small" size={20} />
                  Mise a jour...
                </>
              ) : (
                'Mettre a jour'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminDelivery;
